import { useEffect, useState } from 'react';
import type { PhieuNhapKho, PhieuNhapKhoResponse, PhieuNhapKhoInput } from '@/types/schemas/phieu-nhap-kho.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

/**
 * Return type for the usePhieuNhapKho hook
 */
interface UsePhieuNhapKhoReturn {
  phieuNhapKhos: PhieuNhapKho[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addPhieuNhapKho: (newPhieuNhapKho: PhieuNhapKhoInput) => Promise<PhieuNhapKho>;
  updatePhieuNhapKho: (uuid: string, updatedPhieuNhapKho: PhieuNhapKhoInput) => Promise<PhieuNhapKho>;
  deletePhieuNhapKho: (uuid: string) => Promise<void>;
  refreshPhieuNhapKhos: () => Promise<void>;
  getPhieuNhapKhoByCustomer: (customerId: string) => Promise<PhieuNhapKho[]>;
  getPhieuNhapKhoByStatus: (status: string) => Promise<PhieuNhapKho[]>;
  getPhieuNhapKhoBySalesEmployee: (employeeId: string) => Promise<PhieuNhapKho[]>;
  getPhieuNhapKhoByDateRange: (startDate: string, endDate: string) => Promise<PhieuNhapKho[]>;
  getPhieuNhapKhoDetail: (uuid: string) => Promise<[]>;
  searchPhieuNhapKho: (searchParams: any) => Promise<PhieuNhapKho[]>;
  approvePhieuNhapKho: (uuid: string) => Promise<PhieuNhapKho>;
  cancelPhieuNhapKho: (uuid: string) => Promise<PhieuNhapKho>;
  bulkCancelPhieuNhapKho: (
    uuids: string[]
  ) => Promise<{ success_count: number; failed_count: number; failed_uuids: string[] }>;
}

/**
 * Hook for managing PhieuNhapKho (Warehouse Receipt) data
 *
 * This hook provides functions to fetch, create, update, and delete warehouse receipts.
 * It includes various filtering and search capabilities for warehouse receipt management.
 *
 * @example
 * ```tsx
 * const {
 *   phieuNhapKhos,
 *   isLoading,
 *   addPhieuNhapKho,
 *   updatePhieuNhapKho,
 *   getPhieuNhapKhoByCustomer,
 *   searchPhieuNhapKho
 * } = usePhieuNhapKho();
 *
 * // Create a new warehouse receipt
 * const handleCreate = async (data: PhieuNhapKhoInput) => {
 *   try {
 *     const newReceipt = await addPhieuNhapKho(data);
 *     console.log('Created:', newReceipt);
 *   } catch (error) {
 *     console.error('Failed to create:', error);
 *   }
 * };
 *
 * // Search warehouse receipts
 * const handleSearch = async (params: any) => {
 *   const results = await searchPhieuNhapKho(params);
 *   console.log('Search results:', results);
 * };
 * ```
 */
export const usePhieuNhapKho = (initialPhieuNhapKhos: PhieuNhapKho[] = []): UsePhieuNhapKhoReturn => {
  const [phieuNhapKhos, setPhieuNhapKhos] = useState<PhieuNhapKho[]>(initialPhieuNhapKhos);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const { entity } = useAuth();

  /**
   * Fetch all warehouse receipts
   */
  const fetchPhieuNhapKhos = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PhieuNhapKhoResponse>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/`
      );
      setPhieuNhapKhos(response.data.results);
    } catch (error) {
      console.error('Error fetching warehouse receipts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Add a new warehouse receipt
   */
  const addPhieuNhapKho = async (newPhieuNhapKho: PhieuNhapKhoInput): Promise<PhieuNhapKho> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<PhieuNhapKho>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/`,
        newPhieuNhapKho
      );

      const addedPhieuNhapKho = response.data;
      setPhieuNhapKhos(prev => [...prev, addedPhieuNhapKho]);
      return addedPhieuNhapKho;
    } catch (error) {
      console.error('Error adding warehouse receipt:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing warehouse receipt
   */
  const updatePhieuNhapKho = async (uuid: string, updatedPhieuNhapKho: PhieuNhapKhoInput): Promise<PhieuNhapKho> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<PhieuNhapKho>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/${uuid}/`,
        updatedPhieuNhapKho
      );

      const updated = response.data;
      setPhieuNhapKhos(prev => prev.map(item => (item.uuid === uuid ? updated : item)));
      return updated;
    } catch (error) {
      console.error('Error updating sales order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a warehouse receipt
   */
  const deletePhieuNhapKho = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/${uuid}/`);
      setPhieuNhapKhos(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting warehouse receipt:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get warehouse receipts by customer
   */
  const getPhieuNhapKhoByCustomer = async (customerId: string): Promise<PhieuNhapKho[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<PhieuNhapKhoResponse>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching warehouse receipts by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get warehouse receipts by status
   */
  const getPhieuNhapKhoByStatus = async (status: string): Promise<PhieuNhapKho[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<PhieuNhapKhoResponse>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/?status=${status}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching warehouse receipts by status:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get warehouse receipts by sales employee
   */
  const getPhieuNhapKhoBySalesEmployee = async (employeeId: string): Promise<PhieuNhapKho[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<PhieuNhapKhoResponse>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/?ma_nvbh=${employeeId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching warehouse receipts by employee:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get warehouse receipts by date range
   */
  const getPhieuNhapKhoByDateRange = async (startDate: string, endDate: string): Promise<PhieuNhapKho[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<PhieuNhapKhoResponse>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/?ngay_ct_from=${startDate}&ngay_ct_to=${endDate}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching warehouse receipts by date range:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get warehouse receipt detail
   */
  const getPhieuNhapKhoDetail = async (uuid: string): Promise<[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<[]>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/${uuid}/chi-tiet/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse receipt detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  /**
   * Search warehouse receipts with custom parameters
   */
  const searchPhieuNhapKho = async (searchParams: any): Promise<PhieuNhapKho[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const queryString = new URLSearchParams(searchParams).toString();
      const response = await api.get<PhieuNhapKhoResponse>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/?${queryString}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error searching warehouse receipts:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Approve a warehouse receipt
   */
  const approvePhieuNhapKho = async (uuid: string): Promise<PhieuNhapKho> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<PhieuNhapKho>(
        `/entities/${entity.slug}/erp/ton-kho/nhap-kho-noi-bo/phieu-nhap-kho/${uuid}/approve/`
      );

      const approved = response.data;
      setPhieuNhapKhos(prev => prev.map(item => (item.uuid === uuid ? approved : item)));
      return approved;
    } catch (error) {
      console.error('Error approving sales order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cancel a sales order
   * After cancelling, refresh data and save automatically
   */
  const cancelPhieuNhapKho = async (uuid: string): Promise<PhieuNhapKho> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<PhieuNhapKho>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_NHAP_KHO}/${uuid}/cancel/`
      );
      const cancelled = response.data;

      await refreshPhieuNhapKhos();

      return cancelled;
    } catch (error) {
      console.error('Error cancelling sales order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Bulk cancel multiple sales orders
   * After cancelling, refresh data and save automatically
   */
  const bulkCancelPhieuNhapKho = async (
    uuids: string[]
  ): Promise<{ success_count: number; failed_count: number; failed_uuids: string[] }> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<{ success_count: number; failed_count: number; failed_uuids: string[] }>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_NHAP_KHO}/bulk-cancel/`,
        { uuids }
      );
      const result = response.data;

      await refreshPhieuNhapKhos();

      return result;
    } catch (error) {
      console.error('Error bulk cancelling sales orders:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh warehouse receipts data
   */
  const refreshPhieuNhapKhos = async (): Promise<void> => {
    await fetchPhieuNhapKhos();
  };

  useEffect(() => {
    fetchPhieuNhapKhos();
  }, [entity?.slug]);

  return {
    phieuNhapKhos,
    isLoading,
    isLoadingDetail,
    addPhieuNhapKho,
    updatePhieuNhapKho,
    deletePhieuNhapKho,
    refreshPhieuNhapKhos,
    getPhieuNhapKhoByCustomer,
    getPhieuNhapKhoByStatus,
    getPhieuNhapKhoBySalesEmployee,
    getPhieuNhapKhoByDateRange,
    getPhieuNhapKhoDetail,
    searchPhieuNhapKho,
    approvePhieuNhapKho,
    cancelPhieuNhapKho,
    bulkCancelPhieuNhapKho
  };
};

export const fetchAllPhieuNhapKho = async (
  entitySlug: string,
  params: Record<string, any> = {},
  pageSize = 200
): Promise<PhieuNhapKho[]> => {
  let page = 1;
  let all: PhieuNhapKho[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<PhieuNhapKhoResponse>(`/entities/${entitySlug}/erp/${QUERY_KEYS.PHIEU_NHAP_KHO}/`, {
      params: {
        ...params,
        page,
        page_size: pageSize
      }
    });

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
